import {
  getGetBookingRequestStatusQueryKey,
  useGetBookingRequestStatus,
  StatusBookingRequest,
} from '@anyroad/api';
import type {
  PageOneDataForPaymentDefaults,
  PaymentFormData,
  StripeBillingAddressPrefill,
} from './types';

export const hasLocationAnswer = (value: unknown) =>
  value !== null &&
  typeof value === 'object' &&
  'raw_address' in value &&
  'validation_status' in value;

export const useStatusQuery = (
  pollingBookingRequestId: string,
  pollStartedAtRef: React.RefObject<number>,
  setPollingBookingRequestId: (bookingRequestId: string | null) => void
) =>
  useGetBookingRequestStatus(pollingBookingRequestId, {
    query: {
      queryKey: getGetBookingRequestStatusQueryKey(
        pollingBookingRequestId ?? ''
      ),
      enabled: !!pollingBookingRequestId,
      refetchInterval: (query) => {
        const data = query.state.data as StatusBookingRequest;
        if (data?.committed || data?.webhook_error) return false;
        // If the polling started more than 60 seconds ago, stop polling
        if (
          pollStartedAtRef.current &&
          Date.now() - pollStartedAtRef.current > 60000
        ) {
          setPollingBookingRequestId(null);
          return false;
        }
        return 10000;
      },
    },
  });

export type PaymentPrefillFromPageOne = Pick<
  PaymentFormData,
  'name' | 'email' | 'phone' | 'billingAddress'
>;

/**
 * Maps page-one checkout fields to RHF/Stripe prefill (`name`, `email`, `phone`, `billingAddress`).
 * Spread into page-two `useForm` defaults together with `message` and `is_pay_later_selected`, e.g.:
 *
 * `defaultValues: { message: '', is_pay_later_selected: flag, ...paymentDefaultValuesFromPageOne(pageOne) }`
 *
 * Or use {@link checkoutPageTwoFormDefaultValues} for the same object in one call.
 */
export const paymentDefaultValuesFromPageOne = (
  pageOneData: PageOneDataForPaymentDefaults | null | undefined
): PaymentPrefillFromPageOne => {
  if (!pageOneData) return {};
  const first = pageOneData.first_name ?? '';
  const last = pageOneData.last_name ?? '';
  const name =
    [first, last].filter(Boolean).join(' ') || pageOneData.name || undefined;
  const email = pageOneData.email || undefined;
  const phone = pageOneData.phone || undefined;
  const address: StripeBillingAddressPrefill = {};
  if (pageOneData.billing_address_line_1)
    address.line1 = pageOneData.billing_address_line_1;
  if (pageOneData.billing_address_line_2)
    address.line2 = pageOneData.billing_address_line_2;
  if (pageOneData.billing_city) address.city = pageOneData.billing_city;
  if (pageOneData.billing_region) address.state = pageOneData.billing_region;
  if (pageOneData.billing_zip_code)
    address.postal_code = pageOneData.billing_zip_code;
  if (pageOneData.billing_country)
    address.country = pageOneData.billing_country;
  if (Object.keys(address).length === 0) return { name, email, phone };
  return { name, email, phone, billingAddress: address };
};

export type CheckoutPageTwoRhfDefaultValues = Pick<
  PaymentFormData,
  | 'message'
  | 'is_pay_later_selected'
  | 'name'
  | 'email'
  | 'phone'
  | 'billingAddress'
>;

/** Merge Stripe prefill with fixed RHF fields for page two (use with `useForm({ defaultValues })`). */
export const checkoutPageTwoFormDefaultValuesFromPaymentPrefill = (
  paymentPrefill: PaymentPrefillFromPageOne,
  isPayLaterSelected: boolean
): CheckoutPageTwoRhfDefaultValues => ({
  message: '',
  is_pay_later_selected: isPayLaterSelected,
  ...paymentPrefill,
});

/** When you only have page-one data: same shape as `checkoutPageTwoFormDefaultValuesFromPaymentPrefill(paymentDefaultValuesFromPageOne(…), …)`. */
export const checkoutPageTwoFormDefaultValues = (
  pageOneData: PageOneDataForPaymentDefaults | null | undefined,
  isPayLaterSelected: boolean
): CheckoutPageTwoRhfDefaultValues =>
  checkoutPageTwoFormDefaultValuesFromPaymentPrefill(
    paymentDefaultValuesFromPageOne(pageOneData),
    isPayLaterSelected
  );
